import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { isNullOrUndefined, MessageService } from '@sla-ng/core';
import { IMessageValue } from '@sla-ng/core/lib/message/message-value.model';
import { Moment } from 'moment';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { IAddPlannedSaleVolume } from '../../../models/add-planned-sale-volume.model';
import { IPlannedSaleVolume } from '../../../models/planned-sale-volume.model';
import { IPlanningGenerationSelection } from '../../../models/planning-generation-selection.model';
import { IUpdatePlannedSaleVolume } from '../../../models/update-planned-sale-volume.model';
import { PlannedSaleVolumeResource } from '../../../resources/planned-sale-volume.resource';
import { IPlannedSaleVolumeState } from './planned-sale-volume-state.model';

let _state: IPlannedSaleVolumeState = {
    isLoading: false,
    data: [],
};

@Injectable({
    providedIn: 'root',
})
export class PlannedSaleVolumeFacade {
    protected store$: BehaviorSubject<IPlannedSaleVolumeState> = new BehaviorSubject<IPlannedSaleVolumeState>(_state);

    constructor(private myTranslateService: TranslateService,
                private myPlannedSaleVolumeResource: PlannedSaleVolumeResource,
                private myMessageService: MessageService) {
    }

    public getStateSnapshot(): IPlannedSaleVolumeState {
        return this.store$.value;
    }

    public subState(): Observable<IPlannedSaleVolumeState> {
        return this.store$.asObservable();
    }

    public clearState(): void {
        this.updateState({
            isLoading: false,
            data: [],
        });
    }

    public async fetchPlannedSaleVolumes(costUnitId?: number, from?: Moment, to?: Moment): Promise<void> {
        this.setIsloading(true);
        const data: IPlannedSaleVolume[] = await firstValueFrom(this.myPlannedSaleVolumeResource.getPlannedSaleVolumes(costUnitId, from, to)).catch(() => []);
        this.updateState({ ..._state, data });
        this.setIsloading(false);
    }

    public async addPlannedSaleVolume(data: IAddPlannedSaleVolume): Promise<void> {
        this.setIsloading(true);
        const createdPrice: IPlannedSaleVolume = await firstValueFrom(this.myPlannedSaleVolumeResource.addPlannedSaleVolume(data)).catch(() => null);
        if (createdPrice !== null) {
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.planned-sale-volume.actions.added.label');
        }
        this.setIsloading(false);
    }

    public async generatePlannedVolumes(generationSelection: IPlanningGenerationSelection): Promise<void> {
        this.setIsloading(true);
        const generationSummary = await firstValueFrom(this.myPlannedSaleVolumeResource.generateVolumes(generationSelection)).catch(() => null);
        if (!isNullOrUndefined(generationSummary)) {
            this.myTranslateService.instant('app.page.planning.actions.generated.label', generationSummary);
            const message: IMessageValue = { value: 'app.page.planning.actions.generated.label', translationParams: generationSummary };
            this.myMessageService.successTrans('app.actions.feedback.saved', message, 4);
        }
        this.setIsloading(false);
    }

    public async deletePlannedSaleVolume(price: IPlannedSaleVolume): Promise<void> {
        this.setIsloading(true);
        try {
            await firstValueFrom(this.myPlannedSaleVolumeResource.deletePlannedSaleVolume(price.id));
            this.myMessageService.successTrans('app.actions.feedback.deleted', 'app.page.planned-sale-volume.actions.deleted.label');
        } catch (e) {
            console.error(e);
        }
        this.setIsloading(false);
    }

    public async updatePlannedSaleVolume(id: number, data: IUpdatePlannedSaleVolume): Promise<void> {
        this.setIsloading(true);
        const updatedPrice: IPlannedSaleVolume = await firstValueFrom(this.myPlannedSaleVolumeResource.updatePlannedSaleVolume(id, data)).catch(() => null);
        if (updatedPrice !== null) {
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.planned-sale-volume.actions.edited.label');
        }
        this.setIsloading(false);
    }

    private updateState(state: IPlannedSaleVolumeState): void {
        this.store$.next(_state = state);
    }

    private setIsloading(isLoading: boolean): void {
        this.updateState({ ..._state, isLoading });
    }
}
